<template>
  <b-modal
    id="create-customer-modal"
    title="Create Customer"
    cancel-variant="outline-secondary"
    centered
    hide-footer
    size="sm"
    @hidden="resetForm"
  >
    <!-- ── Form view ─────────────────────────────────────────────────────── -->
    <template v-if="view === 'form'">
      <p class="text-muted small mb-1">
        This LINE contact will be linked to the new customer record automatically.
      </p>

      <b-form-group label="Name" label-for="cc-firstname">
        <b-form-input id="cc-firstname" v-model="form.firstname" placeholder="First name" />
      </b-form-group>

      <b-form-group label="Last name (optional)" label-for="cc-lastname">
        <b-form-input id="cc-lastname" v-model="form.lastname" placeholder="Last name" />
      </b-form-group>

      <b-form-group label="Phone (optional)" label-for="cc-phone">
        <b-form-input
          id="cc-phone"
          v-model="form.phone"
          placeholder="e.g. 0812345678"
          :state="phoneError ? false : null"
          @input="phoneError = ''"
        />
        <small v-if="phoneError" class="text-danger">{{ phoneError }}</small>
      </b-form-group>

      <b-form-group label="Email (optional)" label-for="cc-email">
        <b-form-input
          id="cc-email"
          v-model="form.email"
          type="email"
          placeholder="customer@email.com"
          :state="emailError ? false : null"
          @input="emailError = ''"
        />
        <small v-if="emailError" class="text-danger">{{ emailError }}</small>
      </b-form-group>

      <b-form-group label="LINE ID" label-for="cc-line">
        <b-form-input id="cc-line" :value="selectedUser && selectedUser.user_id" disabled />
      </b-form-group>

      <div class="d-flex justify-content-end mt-1" style="gap:8px">
        <b-button variant="outline-secondary" size="sm" @click="$bvModal.hide('create-customer-modal')">Cancel</b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!form.firstname.trim() || checking || creating"
          @click="createCustomer"
        >
          <b-spinner v-if="checking" small />
          <span v-else>Create</span>
        </b-button>
      </div>
    </template>

    <!-- ── Existing customer found: confirm before linking ──────────────── -->
    <template v-else>
      <p class="mb-1">
        A customer already exists in the system with this {{ matchedByLabel }} — currently chatting
        via LINE as "{{ (selectedUser && selectedUser.display_name) || 'Unknown' }}". Which name should
        we keep?
      </p>

      <label class="cc-name-option" :class="{ 'cc-name-option--active': nameChoice === 'existing' }">
        <input type="radio" value="existing" v-model="nameChoice">
        <div>
          <div class="cc-name-option__label">Keep existing name</div>
          <div class="cc-name-option__value">{{ matchedCustomerName }}</div>
        </div>
      </label>

      <label class="cc-name-option" :class="{ 'cc-name-option--active': nameChoice === 'new' }">
        <input type="radio" value="new" v-model="nameChoice">
        <div>
          <div class="cc-name-option__label">Use new name</div>
          <div class="cc-name-option__value">{{ typedCustomerName }}</div>
        </div>
      </label>

      <div class="d-flex justify-content-end mt-1" style="gap:8px">
        <b-button variant="outline-secondary" size="sm" :disabled="creating" @click="backToForm">Cancel</b-button>
        <b-button variant="primary" size="sm" :disabled="creating" @click="submitCreate">
          <b-spinner v-if="creating" small />
          <span v-else>Confirm &amp; Link</span>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from '@axios'
import { getUserData } from '@/auth/utils'
import {
  BModal, BButton, BFormGroup, BFormInput, BSpinner,
} from 'bootstrap-vue'

const CLIENT_TYPE_ID = 3

export default {
  name: 'CreateCustomerModal',
  components: {
    BModal, BButton, BFormGroup, BFormInput, BSpinner,
  },

  props: {
    selectedUser: { type: Object, default: null },
  },

  data() {
    return {
      checking: false,
      creating: false,
      form: this.emptyForm(),
      emailError: '',
      phoneError: '',
      view: 'form',
      matchedCustomer: null,
      matchedByLabel: '',
      nameChoice: 'existing',
    }
  },

  computed: {
    matchedCustomerName() {
      if (!this.matchedCustomer) return ''
      return [this.matchedCustomer.firstname, this.matchedCustomer.lastname].filter(Boolean).join(' ') || 'Unnamed'
    },
    typedCustomerName() {
      return [this.form.firstname.trim(), this.form.lastname.trim()].filter(Boolean).join(' ')
    },
  },

  methods: {
    emptyForm() {
      return {
        firstname: '', lastname: '', phone: '', email: '',
      }
    },

    open() {
      if (!this.selectedUser) return
      this.form = this.emptyForm()
      this.form.firstname = this.selectedUser.display_name || ''
      this.emailError = ''
      this.phoneError = ''
      this.view = 'form'
      this.matchedCustomer = null
      this.nameChoice = 'existing'
      this.$bvModal.show('create-customer-modal')
    },

    resetForm() {
      this.form = this.emptyForm()
      this.emailError = ''
      this.phoneError = ''
      this.view = 'form'
      this.matchedCustomer = null
      this.nameChoice = 'existing'
    },

    backToForm() {
      this.view = 'form'
      this.matchedCustomer = null
    },

    formatPhoneNumber(phone) {
      const cleaned = phone.replace(/[\s\-().]/g, '')
      if (cleaned.startsWith('+')) return cleaned
      if (cleaned.startsWith('00')) return `+${cleaned.substring(2)}`
      if (cleaned.startsWith('0')) return `+66${cleaned.substring(1)}`
      return cleaned
    },

    async createCustomer() {
      const firstname = this.form.firstname.trim()
      if (!firstname || !this.selectedUser || this.checking || this.creating) return
      this.checking = true
      this.emailError = ''
      this.phoneError = ''

      const email = this.form.email.trim()
      const phone = this.form.phone.trim() ? this.formatPhoneNumber(this.form.phone.trim()) : ''

      try {
        let match = null

        if (email) {
          const res = await axios.post('users/v1/checkemail', JSON.stringify({ email }))
          if (res.data.success !== true) {
            this.emailError = res.data.message || 'This email is already in use'
            return
          }
          if (res.data.data?.id) {
            match = res.data.data
            this.matchedByLabel = 'email'
          }
        }

        if (!match && phone) {
          const res = await axios.post('users/v1/checkphone', JSON.stringify({ phone, phone_name: 'Mobile' }))
          if (res.data.success !== true) {
            this.phoneError = res.data.message || 'This phone number is already in use'
            return
          }
          if (res.data.data?.id) {
            match = res.data.data
            this.matchedByLabel = 'phone number'
          }
        }

        if (match) {
          this.matchedCustomer = match
          this.nameChoice = 'existing'
          this.view = 'confirm'
          return
        }

        await this.submitCreate()
      } finally {
        this.checking = false
      }
    },

    resolvedName() {
      if (this.matchedCustomer && this.nameChoice === 'existing') {
        return {
          firstname: this.matchedCustomer.firstname || '',
          lastname: this.matchedCustomer.lastname || null,
        }
      }
      return {
        firstname: this.form.firstname.trim(),
        lastname: this.form.lastname.trim() || null,
      }
    },

    async submitCreate() {
      if (!this.form.firstname.trim() || !this.selectedUser || this.creating) return
      this.creating = true

      const { firstname, lastname } = this.resolvedName()
      const email = this.form.email.trim()
      const phone = this.form.phone.trim() ? this.formatPhoneNumber(this.form.phone.trim()) : ''
      const userData = getUserData()

      const input = {
        email: email || null,
        emails: email ? [email] : [],
        phone_no: phone || null,
        phone_name: phone ? 'Mobile' : null,
        phones: phone ? [{
          phone_no: phone, phone_name: 'Mobile', phone_label: '', is_primary: true, is_whatsapp: false,
        }] : [],
        password: null,
        firstname,
        lastname,
        nickname: null,
        comment: null,
        address: null,
        country: null,
        preferred_language: null,
        is_active: true,
        type_id: CLIENT_TYPE_ID,
        role: 'user',
        line_contact: this.selectedUser.user_id,
        whatsapp: null,
        created_by: userData?.id,
      }

      try {
        const formData = new FormData()
        formData.append('data', JSON.stringify(input))
        await axios.post('users/v1/add', formData)
        this.$emit('created', this.matchedCustomer
          ? `Linked this LINE chat to existing customer "${firstname}${lastname ? ` ${lastname}` : ''}"`
          : `Customer "${firstname}" created and linked to this chat`, { firstname, lastname })
        this.$bvModal.hide('create-customer-modal')
      } catch (e) {
        this.$emit('error', `Failed to create customer: ${e.response?.data?.message || e.message}`)
      } finally {
        this.creating = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cc-name-option {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover { border-color: #d0cff5; }

  &--active {
    border-color: #7367f0;
    background: #f8f7ff;
  }

  input[type="radio"] {
    cursor: pointer;
    accent-color: #7367f0;
    flex-shrink: 0;
  }
}

.cc-name-option__label {
  font-size: 0.8rem;
  color: #6e6b7b;
}

.cc-name-option__value {
  font-weight: 600;
}
</style>
